import React, { useState, useEffect } from 'react';
import { Card, Button, Alert, Spinner, Badge } from 'react-bootstrap';
import { FaPlus, FaCalendarAlt } from 'react-icons/fa';
import Layout from '../components/Layout';
import AcademicYearTable from '../components/AcademicYearTable';
import AcademicYearFormModal from '../components/AcademicYearFormModal';
import axios from 'axios';
import appConstants from '../constants/appConstants';

const getAuthToken = () => localStorage.getItem('authToken');

const AcademicYears = () => {
  const [alert, setAlert] = useState({ show: false, message: '', variant: '' });
  // Handles input changes for the form fields
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };
  const [academicYears, setAcademicYears] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingYear, setEditingYear] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    short_name: '',
    start_date: '',
    end_date: '',
    status: 'active',
  });

  useEffect(() => {
    fetchAcademicYears();
  }, []);

  const fetchAcademicYears = async () => {
    try {
      const token = getAuthToken();
      const response = await axios.get(
        `${appConstants.API_ROOT_URL}get_academic_years`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (response.data.status === 'success') {
        setAcademicYears(response.data.data || []);
      }
      setLoading(false);
    } catch (err) {
      console.error('Error fetching academic years:', err);
      setAlert({
        show: true,
        message: 'Failed to load academic years',
        variant: 'danger',
      });
      setLoading(false);
    }
  };
  // ...existing code...
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setAlert({ show: false, message: '', variant: '' });
    setSubmitting(true);

    try {
      const token = getAuthToken();
      const instituteId = localStorage.getItem('institute_id') || 1;

      const payload = {
        institute_id: parseInt(instituteId),
        name: formData.name,
        short_name: formData.short_name,
        start_date: formData.start_date,
        end_date: formData.end_date,
        status: formData.status,
      };

      if (editingYear) {
        payload.id = editingYear.id;
      }

      const endpoint = editingYear
        ? 'update_academic_year'
        : 'create_academic_year';

      const response = await axios.post(
        `${appConstants.API_ROOT_URL}${endpoint}`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      if (response.data.status === 'success') {
        setAlert({
          show: true,
          message: editingYear
            ? 'Academic year updated successfully'
            : 'Academic year created successfully',
          variant: 'success',
        });
        setShowModal(false);
        resetForm();
        fetchAcademicYears();
        try {
          window.dispatchEvent(new Event('academicYearsChanged'));
        } catch (e) {
          // ignore when not in browser env
        }
        try {
          window.__refreshAcademicYears && window.__refreshAcademicYears();
        } catch (e) {
          /* ignore */
        }
      }
    } catch (err) {
      console.error('Error saving academic year:', err);
      setAlert({
        show: true,
        message: err.response?.data?.message || 'Failed to save academic year',
        variant: 'danger',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (year) => {
    setEditingYear(year);
    setFormData({
      name: year.name,
      short_name: year.short_name,
      start_date: year.start_date,
      end_date: year.end_date,
      status: year.status,
    });
    setShowModal(true);
  };

  const handleDelete = async (year) => {
    if (!window.confirm(`Are you sure you want to delete "${year.name}"?`)) {
      return;
    }

    try {
      const token = getAuthToken();
      const response = await axios.get(
        `${appConstants.API_ROOT_URL}delete_academic_year?id=${year.id}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      if (response.data.status === 'success') {
        setAlert({
          show: true,
          message: 'Academic year deleted successfully',
          variant: 'success',
        });
        fetchAcademicYears();
        try {
          window.dispatchEvent(new Event('academicYearsChanged'));
        } catch (e) {
          // ignore when not in browser env
        }
        try {
          window.__refreshAcademicYears && window.__refreshAcademicYears();
        } catch (e) {
          /* ignore */
        }
      }
    } catch (err) {
      console.error('Error deleting academic year:', err);
      setAlert({
        show: true,
        message:
          err.response?.data?.message || 'Failed to delete academic year',
        variant: 'danger',
      });
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      short_name: '',
      start_date: '',
      end_date: '',
      status: 'active',
    });
    setEditingYear(null);
    setError('');
  };

  const handleClose = () => {
    setShowModal(false);
    resetForm();
  };

  const getStatusBadge = (status, isCurrent) => {
    // Prefer numeric `is_current` when present (API change)
    if (isCurrent !== undefined && isCurrent !== null) {
      const cur = Number(isCurrent) === 1;
      return (
        <Badge bg={cur ? 'success' : 'secondary'}>
          {cur ? 'Current' : 'Not Current'}
        </Badge>
      );
    }

    // Fallback to textual status
    if (!status) {
      return <Badge bg="secondary">Unknown</Badge>;
    }

    const variants = {
      active: 'success',
      inactive: 'secondary',
      completed: 'info',
      current: 'success',
    };

    const label = status.charAt(0).toUpperCase() + status.slice(1);
    return <Badge bg={variants[status] || 'secondary'}>{label}</Badge>;
  };

  return (
    <Layout>
      <div className="container-fluid mt-4">
        <Card>
          <Card.Header className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0">
              <FaCalendarAlt className="me-2" />
              Academic Years
            </h5>
            <Button
              variant="primary"
              size="sm"
              onClick={() => {
                resetForm();
                setShowModal(true);
              }}
            >
              <FaPlus className="me-1" />
              New Academic Year
            </Button>
          </Card.Header>
          <Card.Body>
            {error && (
              <Alert variant="danger" onClose={() => setError('')} dismissible>
                {error}
              </Alert>
            )}
            {success && (
              <Alert
                variant="success"
                onClose={() => setSuccess('')}
                dismissible
              >
                {success}
              </Alert>
            )}
            {/* Floating Alert (bottom left) */}
            {alert.show && (
              <div
                style={{
                  position: 'fixed',
                  left: '18px',
                  bottom: '18px',
                  zIndex: 9999,
                  minWidth: '180px',
                  maxWidth: '320px',
                  pointerEvents: 'auto',
                  boxShadow: '0 2px 12px rgba(0,0,0,0.12)',
                }}
              >
                <div
                  style={{
                    position: 'relative',
                    borderRadius: '6px',
                    background:
                      alert.variant === 'success'
                        ? '#e6f7e6'
                        : alert.variant === 'danger'
                          ? '#ffeaea'
                          : '#fff8e1',
                    color: '#5c4200',
                    marginBottom: 0,
                    fontWeight: 500,
                    fontSize: '0.92rem',
                    padding: '6px 32px 6px 12px',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                    minHeight: '32px',
                    display: 'flex',
                    alignItems: 'center',
                    maxWidth: '320px',
                  }}
                >
                  <span style={{ flex: 1 }}>{alert.message}</span>
                  <button
                    onClick={() =>
                      setAlert({ show: false, message: '', variant: '' })
                    }
                    style={{
                      position: 'absolute',
                      top: '50%',
                      right: '8px',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      color: '#5c4200',
                      fontWeight: 700,
                      fontSize: '1.1em',
                      cursor: 'pointer',
                      padding: 0,
                    }}
                    aria-label="Close"
                  >
                    ×
                  </button>
                </div>
              </div>
            )}
            <AcademicYearTable
              academicYears={academicYears}
              loading={loading}
              onEdit={handleEdit}
              onDelete={handleDelete}
              getStatusBadge={getStatusBadge}
              onMakeCurrent={async (year) => {
                if (
                  !window.confirm(
                    `Are you sure you want to make "${year.name}" the current academic year?`,
                  )
                )
                  return;
                try {
                  const token = getAuthToken();
                  const instituteId = localStorage.getItem('institute_id') || 1;
                  const payload = {
                    id: year.id,
                    institute_id: parseInt(instituteId, 10),
                    is_current: 1,
                  };
                  await axios.post(
                    `${appConstants.API_ROOT_URL}update_academic_year`,
                    payload,
                    { headers: { Authorization: `Bearer ${token}` } },
                  );
                  setSuccess('Academic year set as current successfully');
                  fetchAcademicYears();
                  try {
                    window.dispatchEvent(new Event('academicYearsChanged'));
                  } catch (e) {}
                  try {
                    window.__refreshAcademicYears &&
                      window.__refreshAcademicYears();
                  } catch (e) {}
                } catch (err) {
                  setError('Failed to set academic year as current');
                }
              }}
            />
          </Card.Body>
        </Card>
        <AcademicYearFormModal
          show={showModal}
          onHide={handleClose}
          onSubmit={handleSubmit}
          formData={formData}
          onInputChange={handleInputChange}
          submitting={submitting}
          editingYear={editingYear}
          error={error}
        />
      </div>
    </Layout>
  );
};

export default AcademicYears;
